import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

/**
 * CurrencyContext — single source of truth for the active display
 * currency across the entire site (booking widget, sticky summary,
 * pricing page, hotel/camp rate strips, spa menu, services, etc.).
 *
 * Base = MAD (Moroccan Dirham). Every price stored anywhere in the
 * codebase (whether the source is MAD, EUR or a free-form string like
 * "150 MAD" / "€95 / pers.") is normalised to MAD first, then
 * converted to the active currency for display.
 *
 * Conversion rates are kept as static snapshots maintained in code —
 * Grup Xaluca's brand team refreshes them manually. Rates are
 * conservative; the disclaimer near the switcher makes it clear that
 * the final invoice is always issued in MAD.
 */

// 1 MAD = X (target currency). Conservative snapshots; refreshable by the brand team.
export const FX_FROM_MAD = {
  MAD: 1,
  EUR: 0.0935,   // ~10.7 MAD per EUR
  USD: 0.10,     // ~10.0 MAD per USD
};

// 1 EUR = X MAD — used to convert EUR-priced data (admin pricing table,
// hard-coded "€95" strings inside hotelDetails) up into the MAD base.
export const EUR_PER_MAD = FX_FROM_MAD.EUR;       // = 1 MAD in EUR
export const MAD_PER_EUR = 1 / FX_FROM_MAD.EUR;   // ≈ 10.7

const CURRENCY_META = {
  MAD: { code: "MAD", label: "MAD", symbol: "MAD", after: true },
  EUR: { code: "EUR", label: "EUR", symbol: "€",   after: false },
  USD: { code: "USD", label: "USD", symbol: "$",   after: false },
};

export const CURRENCY_LIST = [
  CURRENCY_META.MAD,
  CURRENCY_META.EUR,
  CURRENCY_META.USD,
];

const STORAGE_KEY = "xaluca:currency";
const DEFAULT_CURRENCY = "EUR";

// Pull the initial currency from localStorage so the choice persists across navigations + refreshes.
const readInitialCurrency = () => {
  try {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (stored && CURRENCY_META[stored]) return stored;
  } catch (_) { /* SSR / private mode */ }
  return DEFAULT_CURRENCY;
};

const CurrencyContext = createContext({
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
  currencies: CURRENCY_LIST,
});

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(readInitialCurrency);

  // Persist whenever the choice changes.
  useEffect(() => {
    try {
      if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, currency);
    } catch (_) { /* ignore */ }
  }, [currency]);

  const setCurrency = useCallback((code) => {
    if (CURRENCY_META[code]) setCurrencyState(code);
  }, []);

  const value = useMemo(
    () => ({ currency, setCurrency, currencies: CURRENCY_LIST, meta: CURRENCY_META[currency] }),
    [currency, setCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext);

// ── Pure helpers (currency-aware) ───────────────────────────────────

// Round to a clean human number per currency:
//   MAD → nearest 10 (avoids "1 247 MAD" — users expect "1 250 MAD")
//   EUR/USD → nearest whole unit
const roundForCurrency = (value, code) => {
  if (!Number.isFinite(value)) return null;
  if (code === "MAD") return Math.round(value / 10) * 10;
  return Math.round(value);
};

const formatNumber = (n) => {
  if (n == null || !Number.isFinite(n)) return "";
  // Thin non-breaking space as thousands separator — works for all three locales.
  return n.toLocaleString("en-US").replace(/,/g, "\u202F");
};

/**
 * Convert a MAD amount to the active currency and return a formatted string.
 *   1700 MAD → currency=EUR → "€159"
 *   1700 MAD → currency=MAD → "1 700 MAD"
 */
export const formatFromMAD = (madAmount, currencyCode) => {
  if (madAmount === null || madAmount === undefined || madAmount === "") return null;
  const n = Number(madAmount);
  if (!Number.isFinite(n)) return null;
  const code = CURRENCY_META[currencyCode] ? currencyCode : DEFAULT_CURRENCY;
  const meta = CURRENCY_META[code];
  const converted = n * FX_FROM_MAD[code];
  const rounded = roundForCurrency(converted, code);
  if (rounded === null) return null;
  return meta.after
    ? `${formatNumber(rounded)} ${meta.symbol}`
    : `${meta.symbol}${formatNumber(rounded)}`;
};

/**
 * Convert an EUR amount to the active currency.
 * Used by the admin /pricing data + any "€…" hard-coded prices.
 */
export const formatFromEUR = (eurAmount, currencyCode) => {
  if (eurAmount === null || eurAmount === undefined || eurAmount === "") return null;
  const n = Number(eurAmount);
  if (!Number.isFinite(n)) return null;
  const madEquivalent = n * MAD_PER_EUR;
  return formatFromMAD(madEquivalent, currencyCode);
};

/**
 * Parse a free-form price string and return a fully-converted display
 * string in the active currency. Handles both single-amount inputs
 * ("150 MAD", "€95 / pers.") and multi-amount strings like
 * "€23 / 450 MAD" — every numeric+currency token is converted while
 * non-price text (units, slashes, "/ pers.") is preserved verbatim.
 */
export const parseAndConvertPrice = (raw, currencyCode) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  const cleanNumber = (str) =>
    parseFloat(String(str).replace(/[\s\u00A0\u202F,]/g, "").replace(/,/g, "."));

  // Regex captures every price token in the string. Three shapes:
  //   1. "1 250 MAD" / "1,250 DH"     — number-then-currency
  //   2. "€95" / "€ 95"                — currency-then-number
  //   3. "$120"                        — currency-then-number
  // Capture groups: g1=number+MAD/DH, g2=€ number, g3=$ number.
  const tokenRegex = /([\d.,\u00A0\u202F\s]+)\s*(MAD|DH|dh)\b|€\s*([\d.,\u00A0\u202F\s]+)|\$\s*([\d.,\u00A0\u202F\s]+)/g;

  let matched = false;
  const converted = s.replace(tokenRegex, (full, madNum, _madCur, eurNum, usdNum) => {
    let madAmount = null;
    if (madNum && cleanNumber(madNum) > 0) {
      madAmount = cleanNumber(madNum);
    } else if (eurNum && cleanNumber(eurNum) > 0) {
      madAmount = cleanNumber(eurNum) * MAD_PER_EUR;
    } else if (usdNum && cleanNumber(usdNum) > 0) {
      madAmount = cleanNumber(usdNum) * (1 / FX_FROM_MAD.USD);
    }
    if (madAmount == null) return full;
    matched = true;
    return formatFromMAD(madAmount, currencyCode) || full;
  });

  // If at least one token was converted, return the rewritten string.
  if (matched) return converted.replace(/\s+/g, " ").trim();

  // Otherwise treat the entire input as a plain number → MAD.
  const num = cleanNumber(s);
  if (Number.isFinite(num) && num > 0) {
    return formatFromMAD(num, currencyCode);
  }
  return null;
};

/**
 * Hook returning currency-aware formatters bound to the current
 * context. Components should prefer this over the raw helpers so
 * they automatically re-render when the user flips the switcher.
 */
export const useFormatPrice = () => {
  const { currency } = useCurrency();
  return useMemo(
    () => ({
      currency,
      fromMAD: (mad) => formatFromMAD(mad, currency),
      fromEUR: (eur) => formatFromEUR(eur, currency),
      parse: (raw) => parseAndConvertPrice(raw, currency),
    }),
    [currency]
  );
};

export default CurrencyContext;
